/* global removeTrapFocus */

const MainSearch = class extends HTMLElement {
  constructor() {
    super();

    // Clicking close button
    this.querySelectorAll('.main-search__close').forEach((el) => {
      el.addEventListener('click', (evt) => {
        evt.preventDefault();
        document.body.classList.remove('show-search');
        if (typeof removeTrapFocus === 'function') {
          removeTrapFocus(theme.trapOpener);
        }
      });
    });

    // Pressing escape key anywhere inside search overlay
    this.addEventListener('keyup', (evt) => {
      if (evt.key === 'Escape') {
        const closeBtn = this.querySelector('.main-search__close');
        if (closeBtn) closeBtn.dispatchEvent(new Event('click'));
      }
    });

    // Search as you type
    if (this.dataset.quickSearch === 'true') {
      this.initQuickSearch();
    }

    // Result navigation
    this.activeResultIndex = -1;
    const inputEl = this.querySelector('.main-search__input');
    if (inputEl) {
      inputEl.addEventListener('keydown', this.handleKeyDown.bind(this));
    }
  }

  handleKeyDown(evt) {
    const options = [...this.querySelectorAll('.main-search__results [role="option"]')];
    if (!options.length) return;

    const updateActiveOption = () => {
      options.forEach((opt) => opt.classList.remove('search-result-active'));
      const active = options[this.activeResultIndex];
      if (active) {
        active.classList.add('search-result-active');
        evt.currentTarget.setAttribute('aria-activedescendant', active.id);
        active.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    };

    if (evt.key === 'ArrowDown') {
      evt.preventDefault();
      this.activeResultIndex = Math.min(this.activeResultIndex + 1, options.length - 1);
      updateActiveOption();
    }

    if (evt.key === 'ArrowUp') {
      evt.preventDefault();
      this.activeResultIndex = Math.max(this.activeResultIndex - 1, 0);
      updateActiveOption();
    }

    if (evt.key === 'Enter' && this.activeResultIndex >= 0 && options[this.activeResultIndex]) {
      evt.preventDefault();
      const targetLink = options[this.activeResultIndex].querySelector('a');
      if (targetLink) targetLink.click();
    }
  }

  initQuickSearch() {
    const searchInput = this.querySelector('.main-search__input');
    if (!searchInput) return;

    const clearBtn = this.querySelector('.main-search__clear');
    const searchTimeoutThrottle = 200;
    const resultLimit = 8;
    const includeMeta = this.dataset.quickSearchMeta === 'true';
    let searchTimeoutID = -1;
    let searchAbortController = null;

    const updateHasValueState = () => {
      this.classList.toggle('has-value', searchInput.value.trim().length > 0);
    };

    const handleInputChange = (immediate = false) => {
      const resultsBox = this.querySelector('.main-search__results');
      const valueToSearch = searchInput.value.trim();
      updateHasValueState();

      // Only search if search string has content and has changed
      if (valueToSearch.length && valueToSearch !== this.oldSearchValue) {
        this.oldSearchValue = valueToSearch;

        // Kill outstanding ajax request
        if (searchAbortController !== null) {
          searchAbortController.abort('Existing request not needed');
          searchAbortController = null;
        }

        // Kill previous search timer
        clearTimeout(searchTimeoutID);

        // Create URL for full search results
        const form = searchInput.closest('form');
        const linkURL = new URL(form.action);
        const formParams = new URLSearchParams(new FormData(form));
        formParams.forEach((value, key) => { linkURL.searchParams.set(key, value); });
        linkURL.searchParams.set('q', valueToSearch);

        // Show loading
        this.classList.remove('main-search--has-results', 'main-search--results-on-multiple-lines', 'main-search--no-results');
        this.classList.add('main-search--loading');
        if (!this.querySelector('.main-search__results-spinner')) {
          resultsBox.innerHTML = '<div class="main-search__results-spinner"><div class="loading-spinner"></div></div>';
        }

        // Do next search (in X milliseconds, or immediately on pill click)
        const delay = immediate === true ? 10 : searchTimeoutThrottle;
        searchTimeoutID = setTimeout(() => {
          searchAbortController = new AbortController();
          let ajaxUrl;

          if (theme.Shopify.features.predictiveSearch) {
            ajaxUrl = new URL(theme.routes.base + theme.routes.predictiveSearch);
            ajaxUrl.searchParams.set('q', valueToSearch);
            ajaxUrl.searchParams.set('section_id', 'predictive-search');
            ajaxUrl.searchParams.set('resources[limit]', resultLimit);
            ajaxUrl.searchParams.set('resources[limit_scope]', 'each');
            ajaxUrl.searchParams.set('resources[options][fields]', includeMeta ? 'title,product_type,variants.title,vendor,tag,variants.sku' : 'title,product_type,variants.title,vendor');
          } else {
            ajaxUrl = new URL(linkURL);
            ajaxUrl.searchParams.set('section_id', 'main-search');
          }

          fetch(ajaxUrl, { method: 'get', signal: searchAbortController.signal })
            .then((response) => {
              if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
              }
              return response.text();
            })
            .then((response) => {
              const template = document.createElement('template');
              template.innerHTML = response;
              let resultsList = null;
              let resultsMessage = null;
              let topBar = null;
              let pagesHeadingEl = null;

              if (theme.Shopify.features.predictiveSearch) {
                resultsList = template.content.querySelector('.product-grid');
                resultsMessage = template.content.querySelector('[data-results-message]');
                topBar = template.content.querySelector('.predictive-search__top-bar');
                pagesHeadingEl = template.content.querySelector('[data-pages-heading]');
              } else {
                resultsList = template.content.querySelector('.section-search-template .product-grid');
                resultsMessage = template.content.querySelector('.section-search-template [data-results-message]');
              }

              // Reset previous contents
              searchAbortController = null;
              this.classList.remove('main-search--has-results', 'main-search--results-on-multiple-lines', 'main-search--no-results');
              resultsBox.innerHTML = '';

              const resultsProducts = document.createElement('div');
              resultsProducts.className = 'main-search__results__products collection-listing';
              resultsProducts.innerHTML = '<div></div>';
              const resultsPages = document.createElement('div');
              resultsPages.className = 'main-search__results__pages';
              const resultsPagesGrid = document.createElement('div');
              resultsPagesGrid.className = 'main-search__pages-grid';
              resultsPages.appendChild(resultsPagesGrid);

              if (resultsList) {
                resultsProducts.firstElementChild.className = resultsList.className;

                // Convert standard blocks into quick search result format
                resultsList.querySelectorAll('.product-block').forEach((block, index) => {
                  block.setAttribute('role', 'option');
                  block.id = `SearchResult${index}`;
                });

                resultsList.querySelectorAll('.product-block:not(.collection-block):not(.page-block)').forEach((block, index) => {
                  if (index < resultLimit) {
                    block.classList.add('main-search-result');
                    block.querySelectorAll('.btn.quickbuy-toggle').forEach((el) => el.remove());
                    block.querySelectorAll('.quickbuy-toggle').forEach((el) => el.classList.remove('quickbuy-toggle'));
                    resultsProducts.firstElementChild.appendChild(block);
                  }
                });

                resultsList.querySelectorAll('.product-block.page-block').forEach((block) => {
                  const linkEl = block.querySelector('a');
                  if (!linkEl) return;
                  const item = document.createElement('a');
                  item.className = 'main-search-result main-search-result--page';
                  if (block.classList.contains('page-block--article')) {
                    item.classList.add('main-search-result--article');
                  }
                  item.href = linkEl.href;
                  const badgeText = block.dataset.badge || '';
                  if (badgeText) {
                    const badge = document.createElement('span');
                    badge.className = 'main-search-result__badge';
                    badge.textContent = badgeText;
                    item.appendChild(badge);
                  }
                  const textDiv = document.createElement('div');
                  textDiv.className = 'main-search-result__text';
                  textDiv.innerText = block.querySelector('.page-block__title').innerText;
                  item.appendChild(textDiv);
                  resultsPagesGrid.appendChild(item);
                });
              }

              this.classList.remove('main-search--loading');

              const areProducts = !!resultsProducts.querySelector('.main-search-result');
              const arePages = !!resultsPagesGrid.querySelector('.main-search-result');
              const areTopBar = !!topBar;

              if (areProducts || arePages || areTopBar) {
                const status = document.querySelector('#HeaderSearchResultsStatus');
                if (status && resultsMessage) status.innerHTML = resultsMessage.innerHTML;

                this.classList.add('main-search--has-results');
                this.classList.toggle('main-search--results-on-multiple-lines', resultsProducts.querySelectorAll('.product-block').length > 4);

                if (areTopBar) {
                  resultsBox.appendChild(topBar);
                }

                if (areProducts) {
                  const headerBar = document.createElement('div');
                  headerBar.className = 'main-search__results-header';

                  const titleSpan = document.createElement('span');
                  titleSpan.className = 'main-search__results-title';
                  titleSpan.textContent = (resultsMessage && resultsMessage.dataset.topMatchesHeading)
                    ? resultsMessage.dataset.topMatchesHeading
                    : (resultsMessage ? resultsMessage.textContent : '');
                  headerBar.appendChild(titleSpan);

                  const topAllLink = document.createElement('a');
                  topAllLink.className = 'main-search__results-top-link';
                  topAllLink.href = linkURL;
                  topAllLink.innerHTML = `<span>${theme.strings.generalSearchViewAll}</span> <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>`;
                  headerBar.appendChild(topAllLink);

                  resultsBox.appendChild(headerBar);
                  resultsBox.appendChild(resultsProducts);
                }

                if (arePages) {
                  const heading = document.createElement('h6');
                  heading.className = 'main-search-result__heading';
                  heading.textContent = (pagesHeadingEl && pagesHeadingEl.dataset.pagesHeading)
                    ? pagesHeadingEl.dataset.pagesHeading
                    : theme.strings.generalSearchPages;
                  resultsPages.insertAdjacentElement('afterbegin', heading);
                  resultsBox.appendChild(resultsPages);
                }

                const allLink = document.createElement('a');
                allLink.className = 'main-search__results-all-link btn btn--secondary';
                allLink.href = linkURL;
                allLink.innerHTML = theme.strings.generalSearchViewAll;
                resultsBox.appendChild(allLink);

                if (typeof theme.manuallyLoadImages === 'function') {
                  theme.manuallyLoadImages(resultsBox);
                }

                searchInput.setAttribute('aria-expanded', true);
                this.activeResultIndex = -1;
              } else {
                // No results - show message and keep Zero-State suggestions visible below
                this.classList.add('main-search--no-results');
                const emptyMessage = document.createElement('div');
                emptyMessage.className = 'main-search__empty-message';
                emptyMessage.innerHTML = theme.strings.generalSearchNoResultsWithoutTerms;
                resultsBox.appendChild(emptyMessage);
                searchInput.setAttribute('aria-expanded', false);
              }
            })
            .catch((error) => {
              if (error !== 'Existing request not needed' && error.name !== 'AbortError') {
                console.warn(error); // eslint-disable-line no-console
              }
            });
        }, delay);
      } else if (!valueToSearch.length) {
        // Abandon current search
        this.oldSearchValue = valueToSearch;
        if (searchAbortController !== null) {
          searchAbortController.abort('Existing request not needed');
          searchAbortController = null;
        }
        clearTimeout(searchTimeoutID);

        // Clear results
        this.classList.remove('main-search--has-results', 'main-search--results-on-multiple-lines', 'main-search--loading', 'main-search--no-results');
        resultsBox.innerHTML = '';
        searchInput.setAttribute('aria-expanded', false);
      }
    };

    searchInput.addEventListener('input', () => handleInputChange(false));
    searchInput.addEventListener('keyup', () => handleInputChange(false));
    searchInput.addEventListener('change', () => handleInputChange(false));

    // Clear button handler
    if (clearBtn) {
      clearBtn.addEventListener('click', (evt) => {
        evt.preventDefault();
        searchInput.value = '';
        handleInputChange(true);
        searchInput.focus();
      });
    }

    // Popular search quick-tags & predictive query chips
    this.addEventListener('click', (evt) => {
      const quickTag = evt.target.closest('.search-quick-tag[data-search-term]');
      if (quickTag) {
        evt.preventDefault();
        searchInput.value = quickTag.dataset.searchTerm;
        searchInput.focus();
        handleInputChange(true);
        return;
      }

      const queryChip = evt.target.closest('.predictive-search__chip--query[data-query-term]');
      if (queryChip && !evt.metaKey && !evt.ctrlKey && !evt.shiftKey) {
        evt.preventDefault();
        searchInput.value = queryChip.dataset.queryTerm;
        searchInput.focus();
        handleInputChange(true);
      }
    });
  }
};

window.customElements.define('main-search', MainSearch);
